import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, Trash2 } from 'lucide-react';
import { PRESET_FONTS, loadGoogleFont } from './fonts';

export const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-[#71717a]">{label}</span>
    {children}
  </label>
);

export const inputCls = 'h-9 w-full rounded-lg border border-white/[0.08] bg-[#09090b] px-2.5 text-sm text-white focus:border-[#A855F7] focus:outline-none';

export const NumberInput = ({ value, onChange, step = 1, min, max, testId }) => (
  <input
    type="number"
    data-testid={testId}
    className={`${inputCls} tabular`}
    value={Number.isFinite(value) ? Math.round(value * 100) / 100 : ''}
    step={step}
    min={min}
    max={max}
    onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
  />
);

export const ColorInput = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border border-white/[0.08] bg-transparent p-0.5" data-testid={testId} />
    <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} font-mono uppercase`} />
  </div>
);

export const FontPicker = ({ value, onChange, fonts }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [manage, setManage] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const font = await fonts.upload(file);
      onChange(font.name);
      toast.success(`Font "${font.name}" added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Font upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <select
        value={value}
        data-testid="font-family-select"
        onChange={(e) => { loadGoogleFont(e.target.value); onChange(e.target.value); }}
        className={inputCls}
        style={{ fontFamily: `'${value}'` }}
      >
        {fonts.customFonts.length > 0 && (
          <optgroup label="Your fonts">
            {fonts.customFonts.map((f) => <option key={f.id} value={f.name} style={{ fontFamily: `'${f.name}'` }}>{f.name}</option>)}
          </optgroup>
        )}
        <optgroup label="Library">
          {PRESET_FONTS.map((f) => <option key={f.name} value={f.name} style={{ fontFamily: `'${f.name}'` }}>{f.name}</option>)}
        </optgroup>
      </select>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="upload-font-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg border border-dashed border-white/[0.15] text-xs font-medium text-[#a1a1aa] hover:border-[#EAB308] hover:text-[#EAB308]"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload .otf / .ttf
        </button>
        {fonts.customFonts.length > 0 && (
          <button type="button" onClick={() => setManage(!manage)} className="h-8 rounded-lg px-2 text-xs text-[#71717a] hover:text-white">{manage ? 'Done' : 'Manage'}</button>
        )}
        <input ref={fileRef} type="file" accept=".otf,.ttf,.woff,.woff2" className="hidden" onChange={handleFile} data-testid="font-file-input" />
      </div>
      {manage && (
        <div className="divide-y divide-white/[0.06] rounded-lg border border-white/[0.08]">
          {fonts.customFonts.map((f) => (
            <div key={f.id} className="flex items-center justify-between px-2.5 py-1.5 text-xs">
              <span style={{ fontFamily: `'${f.name}'` }} className="truncate text-white">{f.name}</span>
              <button type="button" onClick={() => fonts.remove(f.id)} className="text-[#71717a] hover:text-[#f43f5e]" aria-label="Remove font"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
